/**
 * 后端枚举的取值域。
 *
 * 沿用 CRM 的 口径：**枚举码由前端给标签**（后端只给码，
 * 前端必须自己出中英文案）；后端返回的**展示字符串**（message、dataScopeName 之类）
 * 一律原样渲染，不翻译。
 */

/** 开户进度（接口 OnboardingProgress，对齐 CRM 附录 A.1） */
export const ONBOARDING_PROGRESSES = [
  'PENDING_SUBMIT', 'PENDING_ACCOUNT', 'MT5_OPENED',
  'SUIT_FAILED_1', 'SUIT_FAILED_2', 'KT_FAILED_1', 'KT_FAILED_2',
  'PENDING_KYC', 'DUP_HOLD', 'REJECTED',
] as const
export type OnboardingProgress = (typeof ONBOARDING_PROGRESSES)[number]

/**
 * 受限模式 8 态（接口 PortalMode）。
 * 优先级 DUP > AGE > SUIT2 > SUIT1 > KT3 > KT2 > KT1 > NORMAL，由后端投影，前端不自行推导。
 *
 * ⚠️ 需求 §7 的模式表里有 KT3，接口 enum 里也有 KT3，但**进度枚举里没有 KT_FAILED_3**——
 * 第 3 次失败直接落 REJECTED（rejection_reason=KT3）。所以 KT3 只能从 portalMode 判，
 * 不能从 onboardingProgress 判。
 */
export const PORTAL_MODES = ['NORMAL', 'KT1', 'KT2', 'KT3', 'SUIT1', 'SUIT2', 'AGE', 'DUP'] as const
export type PortalMode = (typeof PORTAL_MODES)[number]

/**
 * 终态：没有任何重试入口，个人资料只读，仅保留改密。
 * DUP（查重核实中）2026-10-26 起不算终态（D-96，cfd-platform 决策 023）：客户能登录进来等结果，核实放行后进度照常往下走。
 */
export const TERMINAL_MODES: PortalMode[] = ['KT3', 'SUIT2', 'AGE']

/** MT5 账户状态（接口 Mt5AccountStatus，附录 A.2） */
export const MT5_STATUSES = [
  'PENDING', 'ENABLED', 'FROZEN', 'DISABLED', 'CLOSED', 'CHANGING',
] as const
export type Mt5Status = (typeof MT5_STATUSES)[number]

/**
 * 账户能做什么 —— 需求 §8.1 / §14.1 的口径落成数据表，不写散落的 if。
 *
 * ⭐ 三态而不是布尔，是**有意的**（接口 4.0 / 后端答复 #12）：
 *
 *   'yes'    前端可提交
 *   'no'     前端直接禁用入口（后端也会拒）
 *   'server' **前端不判，交后端裁决** —— 入口可选，被禁时接口返 40321，
 *            message 可直接展示（见 errorCodes 的 FUNDING_DIRECTION_BLOCKED）
 *
 * FROZEN（禁止交易）的**入金是 'no'**：产品 2026-09-04 已按 CRM 需求附录 A.2 裁决「禁入金（入口隐藏 + 接口拒绝）、
 * 允出金与报表」，UC 需求 §8.1 的反向口径作废。原先这里也写 'server'（「至今没有裁决」），入金按钮点得进去、
 * 提交才被 40321 拒——2026-10-23 改成直接置灰 / 不列进入金下拉。
 * **出金仍是 'server'**：后端 `cfd.funding.frozen-withdraw-blocked` 是保留的应急反转阀（缺省 false＝允许），
 * 拉闸时前端不用跟着发版；表单上照旧提示「该账户已冻结」。
 */
export type Ability = 'yes' | 'no' | 'server'

export const MT5_ABILITY: Record<Mt5Status, { deposit: Ability; withdraw: Ability; listed: boolean }> = {
  PENDING:  { deposit: 'no',     withdraw: 'no',     listed: true },
  ENABLED:  { deposit: 'yes',    withdraw: 'yes',    listed: true },
  // 禁入金（A.2，入口隐藏）；出金入口给、以后端闸门为准（应急反转阀）
  FROZEN:   { deposit: 'no',     withdraw: 'server', listed: true },
  DISABLED: { deposit: 'no',     withdraw: 'no',     listed: false },
  CLOSED:   { deposit: 'no',     withdraw: 'no',     listed: false },
  // MT5 动作在途，「绝不假冻结」：不展示为冻结，但资金动作等它落定
  CHANGING: { deposit: 'no',     withdraw: 'no',     listed: true },
}

/** 入口是否可点（'server' 也可点 —— 能不能做由后端说了算） */
export function canTry(a: Ability): boolean {
  return a !== 'no'
}

/** 是否需要在表单上提示「该账户可能被拒」（仅 'server'） */
export function isServerDecided(a: Ability): boolean {
  return a === 'server'
}

/** 银行账号审核状态 */
/** REPLACED＝被 CRM 银行户口合并单换成新户口的旧户口（二期 P05）；后端在用户中心已过滤掉，类型上兜住以防万一 */
export const BANK_STATUSES = ['PENDING_REVIEW', 'APPROVED', 'REJECTED', 'REPLACED'] as const
export type BankStatus = (typeof BANK_STATUSES)[number]

/** KYC 记录结果（KycStatusItem.result / .status，空串=未出结果） */
export type KycResult = 'GREEN' | 'RED' | ''
export type KycRecordStatus = 'PASSED' | 'PENDING_MANUAL' | ''

/** 一期币种（需求 §8：AUD / USD） */
export const CURRENCIES = ['AUD', 'USD'] as const
export type Currency = (typeof CURRENCIES)[number]

/** 状态色三档（附录 A） */
export type Tone = 'ok' | 'warn' | 'bad' | 'muted'

export const MT5_STATUS_TONE: Record<Mt5Status, Tone> = {
  PENDING: 'warn',
  ENABLED: 'ok',
  FROZEN: 'warn',
  DISABLED: 'bad',
  CLOSED: 'muted',
  CHANGING: 'warn',
}

export const BANK_STATUS_TONE: Record<BankStatus, Tone> = {
  PENDING_REVIEW: 'warn',
  APPROVED: 'ok',
  REJECTED: 'bad',
  REPLACED: 'muted',
}

/*
 * `facingStatus()` / `CustomerFacingStatus` 已删（全仓零调用）。口径仍然有效、只是没有页面用它：
 * 客户可见的开户状态标签**只有两种**（需求 §3）——「待完成开户」与「开户未通过」，不得出现「待激活 / 未激活」，
 * 也不展示 MT5 账户的激活状态。页面上这件事由 auth.onboarded / auth.terminal 各自判。
 */

/**
 * 客户类型（接口 0.8.0 起随登录响应与 profile/me 下发；二期 P01 起四值）：
 * RETAIL 个人 Retail / SOPH_IND 个人 Sophisticated / WHOLESALE 公司 Wholesale / SOPH_CORP 公司 Sophisticated。
 * 门户分个人版 / 公司版**一律看 clientType**（后端按实体给），这里的四值只用来展示类型、判断零售保护。
 */
export const CUSTOMER_TYPES = ['RETAIL', 'SOPH_IND', 'WHOLESALE', 'SOPH_CORP'] as const
export type CustomerType = (typeof CUSTOMER_TYPES)[number]
/** 公司类型（公司 Wholesale / 公司 Sophisticated）——只在 clientType 缺失时兜底用 */
export const isCorporateType = (t?: string | null): boolean => t === 'WHOLESALE' || t === 'SOPH_CORP'
